import { Check } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { RevealRow } from "@/hooks/use-reveal-flow";
import { PrizeTallyRow } from "./prize-tally-row";
import { collapseRevealRows } from "./reveal-rows";
import {
  tallyContainer,
  tallyContainerStatic,
  tallyRow,
  tallyRowStatic,
} from "./prize-tally-motion";

/**
 * The result of a "Reveal all N" (#3815): every prize the player just revealed, dealt in as one
 * stack instead of N slot-machine spins.
 *
 * A separate window from `PrizeRevealWindow` rather than a mode of it — that file is reel
 * physics (a frozen spin plan, a landed state machine, brake wobble, shine and spark bursts, and
 * ~15 tunables), and a second layout mode would force a "does this apply in tally mode" answer
 * for every one of them. The two share only chrome.
 *
 * There is deliberately **no cross-type total**: free spins are game-specific and prize types
 * mix, so one summed figure would either mislead or be unreadable. The count in the heading is
 * the honest aggregate, and the closing line confirms the thing deferred crediting actually put
 * in doubt — that the prizes are now the player's.
 */
export const PrizeTallyWindow = ({
  rows,
  count,
  onConfirm,
}: {
  rows: RevealRow[];
  /** The batch's snapshot count, not the live queue. */
  count: number;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const tallyRows = useMemo(() => collapseRevealRows(rows), [rows]);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("igwButton.youHaveWon", "You have won!")}
      // `pointer-events-auto` is required, not cosmetic: the widget's shadow host sets
      // `pointer-events: none` and every interactive layer opts back in (see ui/dialog.tsx).
      // Without it the confirm button renders but no click ever lands on it.
      className="pointer-events-auto fixed inset-0 z-10010 flex items-center justify-center p-4"
    >
      <div aria-hidden className="absolute inset-0 bg-black/75" />

      {/* Two elements, not one, and that split is load-bearing: the banner hangs 28px ABOVE the
          card (`-top-7`), while a long tally has to scroll. A single box cannot do both — any
          `overflow-y` other than `visible` makes the element a scroll container, which clips its
          own overflowing children, so the banner was being cut in half. The scroll lives on the
          inner box; the outer one only positions, so it must stay overflow-visible.
          `PrizeRevealWindow` never hit this because its card has no scroller. */}
      <div className="notification-shadow relative w-[min(94vw,614px)] animate-in rounded-2xl bg-background duration-300 zoom-in-95 fade-in motion-reduce:animate-none">
        {/* "You have won!" banner overlapping the card's top edge — the shared identity of every
            reveal surface, so the tally is recognisably the same moment as the single reveal. */}
        <span
          className="absolute -top-7 left-1/2 -rotate-2 rounded-full bg-prizes px-6 py-3 font-heading text-[22px] leading-none font-extrabold whitespace-nowrap text-background shadow-md"
          style={{ transform: "translateX(-50%)" }}
        >
          {t("igwButton.youHaveWon", "You have won!")}
        </span>

        <div className="flex max-h-[90vh] flex-col gap-4 overflow-y-auto overscroll-y-contain rounded-2xl px-7 pt-12 pb-7">
          {/* One live region over the heading and the summary, so a screen reader announces the
              outcome once rather than in two disconnected fragments. */}
          <div aria-live="polite" className="flex flex-col gap-4">
            <h1 className="font-heading text-[26px] leading-tight font-bold text-foreground">
              {t("prizeReveal.wonCount", "You won {{count}} prizes", { count })}
            </h1>

            <motion.div
              className="flex flex-col gap-2"
              variants={
                reduceMotion
                  ? tallyContainerStatic
                  : tallyContainer(tallyRows.length)
              }
              initial="hidden"
              animate="visible"
            >
              {tallyRows.map((row) => (
                <PrizeTallyRow
                  key={row.key}
                  row={row}
                  variants={reduceMotion ? tallyRowStatic : tallyRow}
                />
              ))}
            </motion.div>

            <div className="flex items-center gap-2.5 border-t border-border pt-4">
              <Check
                aria-hidden
                className="size-4 shrink-0 text-prizes"
                strokeWidth={3}
              />
              <span className="font-body text-[14px] font-medium text-foreground-secondary">
                {t(
                  "prizeReveal.allCredited",
                  "All {{count}} credited to your account.",
                  { count }
                )}
              </span>
            </div>
          </div>

          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="bg-gradient-cta mt-1 flex min-h-12 items-center justify-center self-start rounded-full px-8 py-4 font-heading text-[17px] leading-none font-bold text-background transition-[filter] hover:brightness-105"
          >
            {t("prizeReveal.done", "Done")}
          </button>
        </div>
      </div>
    </div>
  );
};
